import * as Result from "@gordonb/result/result";
import { PROJECT_API_URL, PROJECTS_API_URL } from "../client-config.ts";
import { renderRoute } from "../../util/route.ts";
import {
  type ProjectInfo,
  type ProjectListResult,
  ProjectListResultSchema,
  type ProjectResult,
  ProjectResultSchema,
} from "../../models/project.ts";
import {
  type DeleteResult,
  DeleteResultSchema,
} from "../../models/delete-result.ts";
import { type Ulid } from "../../models/ulid.ts";
import { toBearerToken } from "../../util/bearer-token.ts";

export const getProjects = async (
  token: string | undefined,
): Promise<ProjectListResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  try {
    const response = await fetch(PROJECTS_API_URL, {
      method: "GET",
      headers: {
        authorization: toBearerToken(token),
      },
    });
    const json = await response.json();
    return ProjectListResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

export const getProject = async (
  token: string | undefined,
  projectId: Ulid,
): Promise<ProjectResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  try {
    const response = await fetch(
      renderRoute(PROJECT_API_URL, { project: projectId }),
      {
        method: "GET",
        headers: {
          authorization: toBearerToken(token),
        },
      },
    );
    const json = await response.json();
    return ProjectResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

export const upsertProject = async (
  token: string | undefined,
  project: ProjectInfo,
): Promise<ProjectResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  try {
    const response = await fetch(
      renderRoute(PROJECT_API_URL, { project: project.id }),
      {
        method: "PUT",
        headers: {
          authorization: toBearerToken(token),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(project),
      },
    );
    const json = await response.json();
    return ProjectResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

export const deleteProject = async (
  token: string | undefined,
  projectId: Ulid,
): Promise<DeleteResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  try {
    const response = await fetch(
      renderRoute(PROJECT_API_URL, { project: projectId }),
      {
        method: "DELETE",
        headers: {
          authorization: toBearerToken(token),
        },
      },
    );
    const json = await response.json();
    return DeleteResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};
